import { useEffect, useMemo, useState } from "react";
import { Localized, useLocalization } from "@fluent/react";
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Chip, FormControlLabel, IconButton, List, ListItemText, Paper,
    Skeleton, Stack, Switch, TextField, Typography, useMediaQuery
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UndoIcon from "@mui/icons-material/Undo";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { APIError, unknownError } from "../../../../app/appTypes";
import { ServerErrorMsg } from "../../../../app/ServerErrorMsg";
import { LoadUsers } from "../../../../app/AppDataLoader";
import { User, selectAllUsers } from "../../../users/usersSlice";
import {
    CompanyDepartmentDetails, loadCompanyDepartments,
    SaveCompanyDepartmentMemberDto, setCompanyDepartmentMembers
} from "../companyDepartmentsSlice";
import { CancelButton } from "../../../../app/components/CancelButton";
import { SaveButton } from "../../../../app/components/SaveButton";
import { LpField } from "../../../../app/components/LpField";
import { DepartmentCodeBadge } from "../components/DepartmentCodeBadge";

interface CompanyDepartmentMembersDialogProps {
    open: boolean;
    department: CompanyDepartmentDetails;
    onCancel: () => void;
    onSuccessfullySaved: (departments: CompanyDepartmentDetails[]) => void;
}

const userKey = (name: string) => name.toLowerCase();
const userLabel = (user: User) => `${user.firstname ?? ""} ${user.surname ?? ""}`.trim() || user.userName;

const toApiError = (error: unknown): APIError => {
    if (error && typeof error === "object" && "kind" in error &&
        (error.kind === "http" || error.kind === "connection" || error.kind === "unknown")) {
        return error as APIError;
    }
    return unknownError();
};

export const CompanyDepartmentMembersDialog = ({
    open, department, onCancel, onSuccessfullySaved
}: CompanyDepartmentMembersDialogProps) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const allUsers = useAppSelector(selectAllUsers);
    const [departments, setDepartments] = useState<CompanyDepartmentDetails[]>([]);
    const [selectedMembers, setSelectedMembers] = useState<SaveCompanyDepartmentMemberDto[]>([]);
    const [originalMemberNames, setOriginalMemberNames] = useState<string[]>([]);
    const [removedNames, setRemovedNames] = useState<Set<string>>(() => new Set());
    const [saveMismatch, setSaveMismatch] = useState(false);
    const [search, setSearch] = useState("");
    const [memberSearch, setMemberSearch] = useState("");
    const [addingUsers, setAddingUsers] = useState(false);
    const [showAssigned, setShowAssigned] = useState(false);
    const [loadingDepartments, setLoadingDepartments] = useState(true);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<APIError | null>(null);

    useEffect(() => {
        let cancelled = false;
        dispatch(loadCompanyDepartments()).unwrap().then(
            (loaded) => {
                if (cancelled) return;
                const current = loaded.find((item) => item.id === department.id);
                if (!current) {
                    setServerError({ kind: "http", status: 404 });
                } else {
                    setDepartments(loaded);
                    setSelectedMembers((current.members ?? []).map((member) => ({
                        userName: member.userName,
                        isNotificationReceiver: member.isNotificationReceiver
                    })));
                    setOriginalMemberNames((current.members ?? []).map((member) => userKey(member.userName)));
                    setRemovedNames(new Set());
                }
                setLoadingDepartments(false);
            },
            (error: unknown) => {
                if (!cancelled) {
                    setServerError(toApiError(error));
                    setLoadingDepartments(false);
                }
            }
        );
        return () => { cancelled = true; };
    }, [dispatch, department.id]);

    const assignedElsewhere = useMemo(() => {
        const assignments = new Map<string, string>();
        departments.forEach((item) => {
            if (item.id !== department.id) {
                item.members?.forEach((member) => assignments.set(userKey(member.userName), item.name));
            }
        });
        return assignments;
    }, [departments, department.id]);

    const selectedNames = useMemo(
        () => new Set(selectedMembers.map((member) => userKey(member.userName))),
        [selectedMembers]
    );
    const originalNames = useMemo(() => new Set(originalMemberNames), [originalMemberNames]);
    const activeMembers = useMemo(
        () => selectedMembers.filter((member) => !removedNames.has(userKey(member.userName))),
        [selectedMembers, removedNames]
    );
    const usersByName = useMemo(
        () => new Map(allUsers.map((user) => [userKey(user.userName), user])),
        [allUsers]
    );
    const sortedMembers = useMemo(() => [...selectedMembers].sort((a, b) =>
        (usersByName.get(userKey(a.userName))?.email || a.userName).localeCompare(
            usersByName.get(userKey(b.userName))?.email || b.userName
        )
    ), [selectedMembers, usersByName]);
    const visibleMembers = useMemo(() => {
        const query = memberSearch.trim().toLocaleLowerCase();
        return sortedMembers.filter((member) => {
            const user = usersByName.get(userKey(member.userName));
            return `${user ? userLabel(user) : ""} ${member.userName} ${user?.email ?? ""} ${user?.position ?? ""}`
                .toLocaleLowerCase().includes(query);
        });
    }, [sortedMembers, usersByName, memberSearch]);
    const conflicts = activeMembers.filter((member) => assignedElsewhere.has(userKey(member.userName)));
    const departmentLoaded = departments.some((item) => item.id === department.id);
    const availableUsers = useMemo(() => allUsers.filter((user) => {
        if (selectedNames.has(userKey(user.userName))) return false;
        if (!showAssigned && assignedElsewhere.has(userKey(user.userName))) return false;
        const query = search.trim().toLocaleLowerCase();
        return `${userLabel(user)} ${user.userName} ${user.email}`.toLocaleLowerCase().includes(query);
    }).sort((a, b) => a.email.localeCompare(b.email)), [allUsers, selectedNames, assignedElsewhere, search, showAssigned]);

    const handleSave = async () => {
        setSaving(true);
        setServerError(null);
        setSaveMismatch(false);
        try {
            const latest = await dispatch(loadCompanyDepartments()).unwrap();
            setDepartments(latest);
            if (!latest.some((item) => item.id === department.id)) {
                setServerError({ kind: "http", status: 404 });
                return;
            }
            const occupied = new Set(latest
                .filter((item) => item.id !== department.id)
                .flatMap((item) => (item.members ?? []).map((member) => userKey(member.userName))));
            if (activeMembers.some((member) => occupied.has(userKey(member.userName)))) return;

            await dispatch(setCompanyDepartmentMembers({
                id: department.id,
                dto: { members: activeMembers }
            })).unwrap();
            const updated = await dispatch(loadCompanyDepartments()).unwrap();
            const savedDepartment = updated.find((item) => item.id === department.id);
            if (!savedDepartment) {
                setServerError({ kind: "http", status: 404 });
                return;
            }
            const savedMembers = savedDepartment.members ?? [];
            const expectedNames = new Set(activeMembers.map((member) => userKey(member.userName)));
            if (savedMembers.length !== expectedNames.size ||
                savedMembers.some((member) => !expectedNames.has(userKey(member.userName)))) {
                setSaveMismatch(true);
                return;
            }
            onSuccessfullySaved(updated);
        } catch (error) {
            setServerError(toApiError(error));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            fullWidth
            fullScreen={fullScreen}
            maxWidth={addingUsers ? "lg" : "md"}
            PaperProps={{ sx: { height: fullScreen ? "100%" : addingUsers
                ? "min(780px, calc(100dvh - 48px))"
                : "min(620px, calc(100dvh - 48px))" } }}
        >
            <DialogTitle sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                bgcolor: "grey.200", color: "text.primary", fontWeight: "bold"
            }}>
                <DepartmentCodeBadge code={department.code} color={department.color} />
                <Localized id="company-department-members"><span>_Department members</span></Localized>
            </DialogTitle>
            <DialogContent dividers sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                <ServerErrorMsg err={serverError} />
                {conflicts.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        <Localized id="company-department-members-conflict">
                            <span>_Some members belong to another department. Remove them before saving.</span>
                        </Localized>
                    </Alert>
                )}
                {saveMismatch && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        <Localized id="company-department-members-save-mismatch">
                            <span>_The server did not save the expected department members. Please try again.</span>
                        </Localized>
                    </Alert>
                )}
                {loadingDepartments ? <Skeleton height={100} /> : departmentLoaded && (
                    <Box sx={{
                        display: "grid", gridTemplateColumns: {
                            xs: "1fr", sm: addingUsers ? "1fr 1fr" : "1fr"
                        },
                        gridTemplateRows: {
                            xs: addingUsers ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
                            sm: "minmax(0, 1fr)"
                        },
                        gap: 2, flex: 1, minHeight: 0, pt: 1
                    }}>
                        <Paper elevation={2} sx={{
                            display: "flex", flexDirection: "column", minHeight: 0,
                            gridColumn: { sm: addingUsers ? 2 : 1 }, gridRow: { sm: 1 }
                        }}>
                            <Box sx={{
                                p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                                bgcolor: "grey.100", borderBottom: 1, borderColor: "divider"
                            }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {l10n.getString("company-department-selected-members",
                                        { count: activeMembers.length }, `_Department members (${activeMembers.length})`)}
                                </Typography>
                                <Button
                                    size="small"
                                    disabled={saving}
                                    variant={addingUsers ? "outlined" : "contained"}
                                    startIcon={addingUsers ? <ArrowBackIcon /> : <PersonAddIcon />}
                                    onClick={() => setAddingUsers((value) => !value)}
                                >
                                    {addingUsers
                                        ? l10n.getString("company-department-back-to-members", null, "_Back to members list")
                                        : l10n.getString("company-department-add-users", null, "_Add users")}
                                </Button>
                            </Box>
                            <Box sx={{ px: 2, py: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={memberSearch}
                                    onChange={(event) => setMemberSearch(event.target.value)}
                                    label={l10n.getString("company-department-search-members", null, "_Search department members")}
                                />
                            </Box>
                            <LoadUsers component={<Skeleton height={120} />}>
                                <List dense sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 0 }}>
                                {selectedMembers.length === 0 && (
                                    <Typography sx={{ p: 2 }} variant="body2" color="text.secondary">
                                        <Localized id="no-users-selected"><span>_No users selected</span></Localized>
                                    </Typography>
                                )}
                                {selectedMembers.length > 0 && visibleMembers.length === 0 && (
                                    <Typography sx={{ p: 2 }} variant="body2" color="text.secondary">
                                        <Localized id="company-department-no-matching-members">
                                            <span>_No members match your search</span>
                                        </Localized>
                                    </Typography>
                                )}
                                {visibleMembers.map((member, index) => {
                                    const user = usersByName.get(userKey(member.userName));
                                    const assignedTo = assignedElsewhere.get(userKey(member.userName));
                                    const removed = removedNames.has(userKey(member.userName));
                                    const added = !originalNames.has(userKey(member.userName));
                                    return (
                                        <Box key={member.userName} sx={(theme) => ({
                                            px: 2, py: 1, borderBottom: 1, borderColor: "divider",
                                            display: "flex", alignItems: "center", gap: 1,
                                            bgcolor: removed
                                                ? alpha(theme.palette.error.main, 0.12)
                                                : added ? alpha(theme.palette.success.main, 0.12) : undefined
                                        })}>
                                            <LpField value={index + 1} sx={{ mr: 1, flexShrink: 0 }} />
                                            <ListItemText
                                                sx={{ minWidth: 0 }}
                                                primary={user ? userLabel(user) : member.userName}
                                                primaryTypographyProps={{
                                                    sx: { textDecoration: removed ? "line-through" : "none" }
                                                }}
                                                secondary={assignedTo
                                                    ? l10n.getString("company-department-user-assigned",
                                                        { department: assignedTo }, `_Assigned to: ${assignedTo}`)
                                                    : [user?.email || member.userName, user?.position].filter(Boolean).join(" | ")}
                                                secondaryTypographyProps={{ color: assignedTo ? "error" : "text.secondary" }}
                                            />
                                            {(removed || added) && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    color={removed ? "error" : "success"}
                                                    label={removed
                                                        ? l10n.getString("company-department-member-removed", null, "_To remove")
                                                        : l10n.getString("company-department-member-added", null, "_Added")}
                                                />
                                            )}
                                            <IconButton
                                                size="small"
                                                disabled={saving}
                                                aria-label={removed
                                                    ? l10n.getString("company-department-restore-member", null, "_Restore member")
                                                    : l10n.getString("company-department-remove-member", null, "_Remove member")}
                                                onClick={() => {
                                                    if (removed) {
                                                        setRemovedNames((prev) => {
                                                            const next = new Set(prev);
                                                            next.delete(userKey(member.userName));
                                                            return next;
                                                        });
                                                    } else if (added) {
                                                        setSelectedMembers((prev) => prev.filter((item) =>
                                                            userKey(item.userName) !== userKey(member.userName)));
                                                    } else {
                                                        setRemovedNames((prev) => new Set(prev).add(userKey(member.userName)));
                                                    }
                                                    setSaveMismatch(false);
                                                }}
                                            >
                                                {removed ? <UndoIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                                </List>
                            </LoadUsers>
                        </Paper>
                        {addingUsers && (
                            <LoadUsers component={<Skeleton height={120} />}>
                                <Paper variant="outlined" sx={{
                                    display: "flex", flexDirection: "column", minHeight: 0,
                                    gridColumn: { sm: 1 }, gridRow: { sm: 1 }
                                }}>
                                    <Box sx={{ p: 2, pb: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {l10n.getString("company-department-available-users", null, "_Available users")}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            label={l10n.getString("company-department-search-user", null, "_Search by name or email")}
                                            sx={{ mt: 1 }}
                                        />
                                        <FormControlLabel
                                            control={<Switch size="small" checked={showAssigned}
                                                onChange={(event) => setShowAssigned(event.target.checked)} />}
                                            label={l10n.getString("company-department-show-assigned", null, "_Show users in other departments")}
                                        />
                                    </Box>
                                    <List dense sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 0 }}>
                                        {availableUsers.length === 0 && (
                                            <Typography sx={{ p: 2 }} variant="body2" color="text.secondary">
                                                <Localized id="company-department-no-available-users">
                                                    <span>_No users matching the filters</span>
                                                </Localized>
                                            </Typography>
                                        )}
                                        {availableUsers.map((user, index) => {
                                            const assignedTo = assignedElsewhere.get(userKey(user.userName));
                                            return (
                                                <Box key={user.id} sx={{
                                                    px: 2, py: 1, display: "flex", alignItems: "center",
                                                    borderBottom: 1, borderColor: "divider", gap: 1
                                                }}>
                                                    <LpField value={index + 1} sx={{ mr: 1.5, flexShrink: 0 }} />
                                                    <ListItemText
                                                        sx={{ minWidth: 0 }}
                                                        primary={`${userLabel(user)} (${user.userName})`}
                                                        secondary={[
                                                            user.email,
                                                            user.position,
                                                            assignedTo && l10n.getString("company-department-user-assigned",
                                                                { department: assignedTo }, `_Assigned to: ${assignedTo}`)
                                                        ].filter(Boolean).join(" | ")}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        disabled={!!assignedTo || saving}
                                                        aria-label={l10n.getString("company-department-add-member", null, "_Add member")}
                                                        onClick={() => {
                                                            setSelectedMembers((prev) =>
                                                                prev.some((member) => userKey(member.userName) === userKey(user.userName))
                                                                    ? prev
                                                                    : [...prev, { userName: user.userName, isNotificationReceiver: false }]);
                                                            setSaveMismatch(false);
                                                        }}
                                                    >
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            );
                                        })}
                                    </List>
                                </Paper>
                            </LoadUsers>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} disabled={!departmentLoaded || conflicts.length > 0} onClick={handleSave} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
